import {
  RECEIVE_CHANGE_SHIPPING_ADDRESS_ERROR,
  RECEIVE_FIELD_VALIDATION_ERRORS,
  RECEIVE_SHIPPING_INFO,
  RECEIVE_SHIPPING_INFO_BY_ID,
  REDIRECT,
  REQUEST_SHIPPING_INFO,
  RESET_SHIPPING_FORM,
  UPDATE_MAILING_ADDRESS_OR_NAME,
  UPDATE_SHIPPING_PHONE
} from 'constants/reduxActions';
import { err, setError } from 'actions/errors';
import { redirectTo } from 'actions/redirect';
import { addresses, changeAddress, deleteAddress } from 'apis/mafia';
import {
  ERROR_NOT_AUTHENTICATED,
  ERROR_REQUEST_VALIDATION,
  fetchApiNotAuthenticatedMiddleware,
  fetchErrorMiddleware,
  fetchMafiaValidationErrorMiddleware
} from 'middleware/fetchErrorMiddleware';
import { prependAppRoot } from 'history/AppRootUtils';
import { setSessionCookies } from 'actions/session';
import { processHeadersMiddleware } from 'middleware/processHeadersMiddlewareFactory';
import { buildAuthenticationRedirectUrl } from 'utils/redirect';
import { isValidMartyPath } from 'helpers/LocationUtils';
import { selectMafiaConfig } from 'selectors/environment';

export function requestShippingInfo() {
  return {
    type: REQUEST_SHIPPING_INFO
  };
}

export function receiveShippingInfo(addresses) {
  return {
    type: RECEIVE_SHIPPING_INFO,
    addresses
  };
}

export function receiveShippingInfoById(address) {
  return {
    type: RECEIVE_SHIPPING_INFO_BY_ID,
    address
  };
}

export function updateMailingAddressOrName({ name, value, suggestedAddress }) {
  return {
    type: UPDATE_MAILING_ADDRESS_OR_NAME,
    name,
    value,
    suggestedAddress
  };
}

export function updateShippingPhone({ name, value }) {
  return {
    type: UPDATE_SHIPPING_PHONE,
    name,
    value
  };
}

export function resetShippingForm() {
  return {
    type: RESET_SHIPPING_FORM
  };
}

export function receiveChangeShippingAddressError(error) {
  return {
    type: RECEIVE_CHANGE_SHIPPING_ADDRESS_ERROR,
    error
  };
}

export function fetchAllAddresses(getAddresses = addresses) {
  return function (dispatch, getState) {
    dispatch(requestShippingInfo());

    const state = getState();
    const {
      cookies,
      router: { location }
    } = getState();
    const mafiaConfig = selectMafiaConfig(state);

    return getAddresses(mafiaConfig, cookies)
      .then(processHeadersMiddleware(setSessionCookies(dispatch, getState)))
      .then(fetchApiNotAuthenticatedMiddleware)
      .then(fetchErrorMiddleware)
      .then(resp => {
        const { addresses } = resp;
        dispatch(receiveShippingInfo(addresses));
      })
      .catch(catchHandlerFactory(dispatch, prependAppRoot('/returns', location)));
  };
}

export function fetchShippingInfo(addressId, returnTo, getAddresses = addresses) {
  return function (dispatch, getState) {
    dispatch(requestShippingInfo());
    const state = getState();
    const {
      cookies,
      router: { location }
    } = state;
    const mafiaConfig = selectMafiaConfig(state);

    return getAddresses(mafiaConfig, cookies)
      .then(processHeadersMiddleware(setSessionCookies(dispatch, getState)))
      .then(fetchApiNotAuthenticatedMiddleware)
      .then(fetchErrorMiddleware)
      .then(resp => {
        const { addresses } = resp;
        if (addressId) {
          const address = addresses.find(address => address.addressId === addressId);
          if (address) {
            dispatch(receiveShippingInfoById(address));
          } else {
            dispatch(setError(err.GENERIC, new Error('Address not found...')));
          }
        } else {
          // called from changeShippingAddress()
          dispatch(receiveShippingInfo(addresses));
          dispatch(redirectTo(prependAppRoot(isValidMartyPath(returnTo) ? returnTo : '/addresses', location)));
        }
      })
      .catch(catchHandlerFactory(dispatch, prependAppRoot(isValidMartyPath(returnTo) ? returnTo : '/addresses', location)));
  };
}

export function deleteShippingAddress(addressId) {
  return function (dispatch, getState) {
    const state = getState();
    const {
      cookies,
      router: { location }
    } = state;
    const mafiaConfig = selectMafiaConfig(state);

    return deleteAddress(mafiaConfig, cookies, addressId)
      .then(processHeadersMiddleware(setSessionCookies(dispatch, getState)))
      .then(fetchApiNotAuthenticatedMiddleware)
      .then(fetchErrorMiddleware)
      .then(resp => {
        dispatch(fetchShippingInfo());
        return resp;
      })
      .catch(catchHandlerFactory(dispatch, prependAppRoot('/addresses', location)));
  };
}

// For SimpleReturns snail mail form
export function getNewAddressId(address, cb, { forceOriginal = false, saveInvalidAddress = true }) {
  return function (dispatch, getState) {
    const state = getState();
    const { cookies } = state;
    const mafiaConfig = selectMafiaConfig(state);

    return changeAddress({ mafiaConfig, address, forceOriginal }, cookies, saveInvalidAddress)
      .then(processHeadersMiddleware(setSessionCookies(dispatch, getState)))
      .then(fetchApiNotAuthenticatedMiddleware)
      .then(fetchMafiaValidationErrorMiddleware)
      .then(fetchErrorMiddleware)
      .then(resp => {
        const { valid, invalidFields } = resp;
        if (!valid && invalidFields?.length) {
          dispatch(
            receiveChangeShippingAddressError({
              extraInformation: invalidFields
            })
          );
        } else {
          cb(resp);
        }
        return resp;
      })
      .catch(e => {
        dispatch(receiveChangeShippingAddressError(e));
      });
  };
}

export function changeShippingAddress(address, returnTo) {
  return function (dispatch, getState) {
    dispatch(requestShippingInfo());

    const state = getState();
    const { cookies } = state;
    const mafiaConfig = selectMafiaConfig(state);

    return changeAddress({ mafiaConfig, address }, cookies)
      .then(processHeadersMiddleware(setSessionCookies(dispatch, getState)))
      .then(fetchApiNotAuthenticatedMiddleware)
      .then(fetchMafiaValidationErrorMiddleware)
      .then(fetchErrorMiddleware)
      .then(resp => {
        const { valid, invalidFields } = resp;
        if (!valid && invalidFields?.length) {
          dispatch(
            receiveChangeShippingAddressError({
              extraInformation: invalidFields
            })
          );
        } else {
          // return to /addresses
          dispatch(fetchShippingInfo('', returnTo));
        }
        return resp;
      })
      .catch(e => {
        dispatch(receiveChangeShippingAddressError(e));
      });
  };
}

export function addFieldValidationErrors(requestExtraInformation) {
  return { type: RECEIVE_FIELD_VALIDATION_ERRORS, requestExtraInformation };
}

export const catchHandlerFactory =
  (dispatch, redirectPath = '') =>
  e => {
    switch (e.id) {
      case ERROR_REQUEST_VALIDATION:
        return dispatch(addFieldValidationErrors(e.extraInformation));
      case ERROR_NOT_AUTHENTICATED:
        return dispatch({
          type: REDIRECT,
          location: buildAuthenticationRedirectUrl(redirectPath)
        });
      default: {
        return dispatch(setError(err.GENERIC, e));
      }
    }
  };
